<template>
	<div v-if="contentLoaded">
		<LinkCopyInput
			:value="transformedLink.FullURL"
		>
			Shortend URL
		</LinkCopyInput>
		<ValidationObserver
			ref="observer"
			v-slot="{ valid }"
			tag="div"
			class="link-details"
			@submit.prevent
		>
			<div class="section-header">
				<div class="section-subtitle is-small is-white">
					Add link details
				</div>
				<div class="section-header">
					<AppButton
						v-if="!changesSaved"
						size="small"
						color="secondary"
						gap="right"
						:disabled="!(valid && hasChanges)"
						@click="saveChanges"
					>
						Save changes
					</AppButton>
					<AppButton
						v-if="changesSaved"
						size="small"
						color="grey"
						item="icon"
						gap="right"
					>
						<img
							src="@/assets/check.png"
							class="small-img"
							alt="check"
						>
						Saved changes
					</AppButton>
					<AppButton
						size="small"
						color="outline-red"
						@click="toggleButton"
					>
						Delete
					</AppButton>
					<div
						v-if="deleteThis"
						class="popup-background"
						@click.self="deleteThis = !deleteThis"
					>
						<div class="popup-overlay">
							<div class="section-description">
								Are you sure you want to delete this shortlink?
							</div>
							<div class="buttons">
								<AppButton
									size="flex"
									color="outline-red"
									@click="deleteLink(transformedLink.Id); onCancel();"
								>
									Delete
								</AppButton>
								<AppButton
									size="flex"
									color="primary"
									@click="deleteThis = !deleteThis"
								>
									Cancel
								</AppButton>
							</div>
						</div>
					</div>
				</div>
			</div>
			<div class="columns">
				<div class="column">
					<div>
						<div class="section-description is-medium">
							Title
						</div>
						<AppInput
							v-model="transformedLink.Title"
							color="underline"
							size="full"
							placeholder="Enter link title"
						/>
					</div>
					<div class="item">
						<div class="section-description is-medium">
							Add countries
						</div>

						<SelectCountryOutput
							:key="transformedLink.Id"
							v-model="selectedCountries"
							multiple
						/>
					</div>
				</div>
				<div class="column">
					<div>
						<div class="section-description is-medium">
							Default URL
						</div>
						<AppInput
							v-model="transformedLink.DefaultURL"
							color="underline"
							size="full"
						/>
					</div>
					<div class="item">
						<div class="section-description is-medium">
							Add tags
						</div>
						<div class="tags-input">
							<input
								v-model="newTag"
								class="tag-input"
								placeholder="Add tag by tag and press enter"
								@keyup.enter="addTag"
							>
							<AppButton
								size="flex-flex"
								color="outline-light"
								:disabled="newTag.length <= 0"
								@click="addTag"
							>
								↵
							</AppButton>
						</div>
					</div>
					<div
						v-if="tags != 0 && transformedLink.Tags != 0"
						class="item-tags"
					>
						<div
							v-for="(tag, $index) in tags"
							:key="$index"
							class="item-tag"
						>
							<div class="section-description is-medium-regular">
								{{ tag }}
							</div>
							<img
								src="@/assets/close.png"
								alt="close"
								@click="removeTag($index)"
							>
						</div>
					</div>
				</div>
			</div>
		</ValidationObserver>
	</div>
</template>

<script>
import SelectCountryOutput from '@/components/SelectCountryOutput.vue';
import { mapActions, mapGetters } from 'vuex';
import LinkCopyInput from '@/components/LinkCopyInput.vue';
import { ValidationObserver } from 'vee-validate';
import { deepCloneObject } from '@/utilities/Helpers';
import isEqual from 'lodash/isEqual';

export default {
	name: 'LinkDetails',
	components: {
		SelectCountryOutput,
		LinkCopyInput,
		ValidationObserver,
	},

	props: {
		link: {
			type: Object,
			required: true,
		},
	},

	data() {
		return {
			deleteThis: false,
			changesSaved: false,
			transformedLink: null,
			Title: '',
			contentLoaded: false,
			newTag: '',
			linkSnapshot: null,
		};
	},

	computed: {
		...mapGetters([
			'historyList',
		]),
		tags: {
			get() {
				return this.transformedLink.Tags ? this.transformedLink.Tags.split(' ') : [];
			},
			set(value) {
				this.transformedLink.Tags = value.join(' ');
			},
		},
		selectedCountries: {
			get() {
				return this.transformedLink.TargetURLs;
			},
			set(value) {
				this.transformedLink.TargetURLs = value;
			},
		},
		hasChanges() {
			return !isEqual(this.transformedLink, this.linkSnapshot);
		},
	},

	watch: {
		transformedLink: {
			deep: true,
			async handler() {
				this.changesSaved = false;
			},
		},
		link: {
			handler() {
				this.transformedLink = { ...this.normalizeValues(this.link) };
				this.setLinkSnapshot();
			},
			immediate: true,
		},
	},

	mounted() {
		this.contentLoaded = true;
		this.fetchHistory();
	},

	methods: {
		...mapActions([
			'saveLink',
			'fetchHistory',
			'deleteLink',
			'setLinkToEdit',
		]),
		async saveChanges() {
			await this.saveLink(this.transformedLink);
			this.changesSaved = true;
			this.setLinkSnapshot();
			this.$notify({
				duration: 6000,
				type: 'info',
				title: '',
				text: 'You saved the linkdetails',
			});
		},
		normalizeValues(object) {
			const normalizedValues = Object.keys(object).reduce((newObject, key) => {
				if (object[key] === null) {
					newObject[key] = '';
					return newObject;
				}

				newObject[key] = object[key];
				return newObject;
			}, {});

			return normalizedValues;
		},
		addTag() {
			if (this.newTag.trim().length === 0) {
				return;
			}

			const tagAlreadyExist = this.tags.find((tag) => tag === this.newTag);

			if (!tagAlreadyExist) {
				this.tags = [...this.tags, this.newTag];
			} else if (tagAlreadyExist) {
				this.$notify({
					duration: 6000,
					type: 'warn',
					title: 'duplicated',
					text: 'This tag already exists',
				});
			}

			this.newTag = '';
		},
		removeTag(index) {
			this.tags = [...this.tags.filter((tag, tagIndex) => tagIndex !== index)];
		},
		onSubmit() {
			this.$emit('submit');
		},
		onCancel() {
			this.setLinkToEdit(null);
		},
		toggleButton() {
			this.deleteThis = true;
		},
		notValid() {
			this.$notify({
				duration: 6000,
				type: 'error',
				title: 'Not saved',
				text: 'Invalid values',
			});
		},
		setLinkSnapshot() {
			this.linkSnapshot = deepCloneObject(this.transformedLink);
		},
	},
};
</script>

<style lang="scss" scoped>
.link-details {
	position: relative;
}

.section-description {
	color: $color-accent-200;
	display: flex;
	align-items: center;

	img {
		margin-right: $gap-1;
	}
}

.popup-background {
	position: absolute;
	display: flex;
	justify-content: center;
	align-items: center;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	width: 100%;
	background-color: rgba($color: $color-accent-600, $alpha: 0.8);
	z-index: 9999999999;

	.popup-overlay {
		background-color: $color-accent-400;
		padding: $gap-4;
		border-radius: 6px;

		.section-description {
			margin-bottom: $gap-4;
			color: $color-accent-100;
		}

		.buttons {
			display: flex;
			justify-content: flex-end;

			:last-child {
				margin-left: $gap-1;
			}
		}
	}
}

.button {
	cursor: pointer;
}

.toggle-button {
	margin-top: $gap-2;
}

.input {
	border: none;
	background-color: transparent;
	padding: $gap-15 0;
	width: 100%;
	color: $color-accent-100;
	font-size: 16px;
	font-weight: $fontWeight-regular;
	border-bottom: 2px solid $color-accent-500;
}

.tags-input {
	display: flex;
	border: none;
	background-color: transparent;
	padding: $gap-1 0;
	width: 100%;
	color: $color-accent-100;
	font-size: 16px;
	font-weight: $fontWeight-regular;
	border-bottom: 2px solid $color-accent-500;
}

.tag-input {
	border: none;
	background-color: transparent;
	width: 100%;
	color: $color-accent-100;
	font-size: 16px;
	font-weight: $fontWeight-regular;
}

.title {
	display: flex;
	align-items: center;
	justify-content: space-between;
}

.item {
	margin-top: $gap-4;
}

.item-tags {
	display: flex;
	flex-wrap: wrap;
}

.item-tag {
	background-color: $color-accent-500;
	border-radius: 8px;
	padding: $gap-1;
	margin-top: $gap-2;
	margin-right: $gap-1;
	display: flex;
	word-wrap: break-word;
	align-items: center;
	width: fit-content;

	.section-description {
		margin-right: $gap-1;
	}
}

.link-details {
	background-color: $color-accent-600;
	padding: $gap-4 $gap-6;
	padding-bottom: $gap-6;
}

.checked-button {
    cursor: text;
}

.section-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 0;
	padding-top: 0;
}

.columns {
	display: flex;
	width: 100%;

	.column {
		width: 50%;
		margin-top: $gap-3;
		margin-right: $gap-4;

		&:last-child {
			margin-right: 0;
		}
	}
}

.is-centered {
	display: flex;
	justify-content: center;
	margin-top: $gap-5;
}
</style>
